import { useMemo } from 'react';
import { Combobox, type ComboboxOption } from '@/shared/ui/combobox';
import { useAiModels } from '../api/use-ai-admin';
import { formatModelPrice } from '../model/format';

const DEFAULT = '__default__';

interface ModelPickerProps {
  value: string | null;
  onChange: (value: string | null) => void;
  /** Com rótulo, mostra a opção "Padrão" (valor `null`, segue o nível de cima). */
  defaultLabel?: string;
  disabled?: boolean;
}

/** Modelos GPT-5.6 da OpenAI, com preço atualizado por 1M de tokens. */
export function ModelPicker({ value, onChange, defaultLabel, disabled }: ModelPickerProps) {
  const { data, isLoading, isError } = useAiModels();

  const options = useMemo<ComboboxOption[]>(() => {
    const catalog = (data?.models ?? []).map((model) => ({
      value: model.id,
      label: model.name,
      description: `${model.id} · ${formatModelPrice(model)}`,
    }));
    // Modelo salvo que saiu do catálogo continua visível para o master trocar.
    if (value && !catalog.some((option) => option.value === value)) {
      catalog.unshift({ value, label: value, description: 'Fora da lista GPT-5.6 — o chat usa o modelo padrão' });
    }
    return defaultLabel ? [{ value: DEFAULT, label: 'Padrão', description: defaultLabel }, ...catalog] : catalog;
  }, [data, value, defaultLabel]);

  return (
    <Combobox
      value={value ?? (defaultLabel ? DEFAULT : '')}
      onChange={(next) => onChange(next === DEFAULT ? null : next)}
      options={options}
      placeholder={isError ? 'Catálogo indisponível' : 'Escolha um modelo'}
      searchPlaceholder="Buscar modelo (ex.: gpt-5.6-luna)"
      loading={isLoading}
      disabled={disabled || (isError && !value)}
    />
  );
}
